/**
 * The controlled profile vocabularies, shared by the Worker schemas that validate them and the
 * client form that offers them. One definition, so the two can never disagree about what a
 * advertiser may say about themselves.
 *
 * Slugs are what the database stores; labels are the product wording and are what renders.
 * Where the two differ (for example `trans` labelled "Transgender") the slug is kept because
 * an applied D1 CHECK constraint already permits it and a CHECK cannot be altered in place.
 * Most lists are expected to grow, so zod is the gate and any database CHECK is the backstop.
 */

export interface FieldOption<S extends string = string> {
  slug: S;
  label: string;
}

export interface FieldVocabulary<S extends string = string> {
  options: readonly FieldOption<S>[];
  /** Non-empty tuple, the shape `z.enum(...)` requires. */
  slugs: [S, ...S[]];
  /** Product wording for a slug; unknown values fall back to a title-cased slug. */
  label: (slug: string) => string;
}

export function vocabulary<S extends string>(
  options: readonly { readonly slug: S; readonly label: string }[],
): FieldVocabulary<S> {
  const bySlug = new Map<string, string>(options.map((option) => [option.slug, option.label]));
  return {
    options,
    slugs: options.map((option) => option.slug) as [S, ...S[]],
    label: (slug) =>
      bySlug.get(slug) ?? slug.replace(/-/g, ' ').replace(/(^|\s)[a-z]/g, (c) => c.toUpperCase()),
  };
}

export const GENDER = vocabulary([
  { slug: 'female', label: 'Female' },
  { slug: 'male', label: 'Male' },
  { slug: 'trans', label: 'Transgender' },
  { slug: 'cd', label: 'CD' },
] as const);

export const SEXUALITY = vocabulary([
  { slug: 'heterosexual', label: 'Hetero' },
  { slug: 'gay', label: 'Gay' },
  { slug: 'lesbian', label: 'Lesbian' },
  { slug: 'bisexual', label: 'Bi' },
] as const);

export const ESCORT_FOR = vocabulary([
  { slug: 'men', label: 'Men' },
  { slug: 'women', label: 'Women' },
  { slug: 'disabled', label: 'Disabled' },
] as const);

export const AT_FIRST_GLANCE = vocabulary([
  { slug: 'small', label: 'Small' },
  { slug: 'big-boobs', label: 'Big Boobs' },
  { slug: 'cute', label: 'Cute' },
  { slug: 'fit', label: 'Fit' },
  { slug: 'tall', label: 'Tall' },
  { slug: 'tiny-waist', label: 'Tiny Waist' },
  { slug: 'big-butt', label: 'Big Butt' },
] as const);
export const MAX_AT_FIRST_GLANCE = 3;

export const HAIR_COLOUR = vocabulary([
  { slug: 'black', label: 'Black' },
  { slug: 'blonde', label: 'Blonde' },
  { slug: 'platinum', label: 'Platinum' },
  { slug: 'brunette', label: 'Brunette' },
  { slug: 'blue', label: 'Blue' },
  { slug: 'pink', label: 'Pink' },
] as const);

export const HAIR_STYLE = vocabulary([
  { slug: 'very-short', label: 'Very Short' },
  { slug: 'short', label: 'Short' },
  { slug: 'shoulder-length', label: 'Shoulder Length' },
  { slug: 'long', label: 'Long' },
  { slug: 'very-long', label: 'Very Long' },
] as const);

export const EYES = vocabulary([
  { slug: 'blue', label: 'Blue' },
  { slug: 'brown', label: 'Brown' },
  { slug: 'black', label: 'Black' },
  { slug: 'green', label: 'Green' },
  { slug: 'hazel', label: 'Hazel' },
  { slug: 'grey', label: 'Grey' },
] as const);

export const BODY_TYPE = vocabulary([
  { slug: 'small', label: 'Small' },
  { slug: 'petite', label: 'Petite' },
  { slug: 'slim', label: 'Slim' },
  { slug: 'athletic', label: 'Athletic' },
  { slug: 'average', label: 'Average' },
  { slug: 'curvy', label: 'Curvy' },
  { slug: 'voluptuous', label: 'Voluptuous' },
  { slug: 'bbw', label: 'BBW' },
  { slug: 'cuddly', label: 'Cuddly' },
] as const);

export const BUST = vocabulary([
  { slug: 'aa-cup', label: 'AA Cup' },
  { slug: 'a-cup', label: 'A Cup' },
  { slug: 'b-cup', label: 'B Cup' },
  { slug: 'c-cup', label: 'C Cup' },
  { slug: 'd-cup', label: 'D Cup' },
  { slug: 'dd-cup', label: 'DD Cup' },
  { slug: 'e-cup', label: 'E Cup' },
  { slug: 'f-cup', label: 'F Cup' },
  { slug: 'g-cup', label: 'G Cup' },
  { slug: 'h-cup', label: 'H Cup' },
  { slug: 'i-cup', label: 'I Cup' },
  { slug: 'j-cup', label: 'J Cup' },
] as const);

export const DRESS_SIZE = vocabulary([
  { slug: 'size-4', label: 'Size 4' },
  { slug: 'size-5', label: 'Size 5' },
  { slug: 'size-6', label: 'Size 6' },
  { slug: 'size-7', label: 'Size 7' },
  { slug: 'size-8', label: 'Size 8' },
  { slug: 'size-9', label: 'Size 9' },
  { slug: 'size-10', label: 'Size 10' },
  { slug: 'size-12', label: 'Size 12' },
  { slug: 'size-14', label: 'Size 14' },
  { slug: 'size-16', label: 'Size 16' },
  { slug: 'size-18', label: 'Size 18' },
  { slug: 'size-20', label: 'Size 20' },
  { slug: 'size-22', label: 'Size 22' },
  { slug: 'size-24', label: 'Size 24' },
  { slug: 'size-24-plus', label: 'Size 24+' },
] as const);

export const ETHNICITY = vocabulary([
  { slug: 'chinese', label: 'Chinese' },
  { slug: 'korean', label: 'Korean' },
  { slug: 'japanese', label: 'Japanese' },
  { slug: 'indian', label: 'Indian' },
  { slug: 'thai', label: 'Thai' },
  { slug: 'caucasian', label: 'Caucasian / White' },
  { slug: 'european', label: 'European' },
  { slug: 'black', label: 'Black' },
  { slug: 'african', label: 'African' },
  { slug: 'latina', label: 'Latina / Latino' },
  { slug: 'asian', label: 'Asian' },
  { slug: 'indigenous', label: 'Indigenous' },
] as const);

/** Stored comma-joined on the advertiser; the API carries them as an array of slugs. */
export const LANGUAGES = vocabulary([
  { slug: 'english', label: 'English' },
  { slug: 'chinese', label: 'Chinese' },
  { slug: 'korean', label: 'Korean' },
  { slug: 'japanese', label: 'Japanese' },
  { slug: 'thai', label: 'Thai' },
  { slug: 'spanish', label: 'Spanish' },
  { slug: 'russian', label: 'Russian' },
] as const);

/** "I might consider…" — the whole list may be chosen. */
export const CONSIDERS = vocabulary([
  { slug: 'filming', label: 'Filming' },
  { slug: 'cimws', label: 'CIMWS' },
  { slug: 'foot-fetish', label: 'Foot Fetish' },
  { slug: 'anal', label: 'Anal' },
  { slug: 'bdsm', label: 'BDSM' },
] as const);

/** "I don't do…" — the whole list may be chosen. */
export const LIMITS = vocabulary([
  { slug: 'anal', label: 'Anal' },
  { slug: 'filming', label: 'Filming' },
  { slug: 'cim', label: 'CIM' },
  { slug: 'cimws', label: 'CIMWS' },
  { slug: 'cof', label: 'COF' },
] as const);

export const AESTHETIC = vocabulary([
  { slug: 'young', label: 'Young' },
  { slug: 'fit', label: 'Fit' },
  { slug: 'elegant', label: 'Elegant' },
  { slug: 'slutty', label: 'Slutty' },
  { slug: 'office', label: 'Office' },
] as const);
export const MAX_AESTHETIC = 2;
